import {useQuery} from "@tanstack/react-query";
import {WasteTile, WasteTileProps} from "../waste/tile.tsx";
import {ChangeEventHandler, useCallback, useMemo, useState} from "react";
import {Input as JoyInput, Stack} from "@mui/joy";
import {ActionButton} from "../components/ActionButton.tsx";
import {useTranslation} from "react-i18next";
import {WastePatch, WasteStampFlavor, watchWasteByIdTqo} from "../rpc/webrpc-client.gen.ts";
import {NumericFormat} from "react-number-format";
import {useMothership, usePatchWasteMutation, useStampWasteMutation} from "../tq/tqx.tsx";
import {TILE_ACTION_BUTTON_SX} from "../components/Tile.tsx";
import {fixedPointToStr, strToFixedPoint} from "../tq/tqutil.tsx";

export function DriverWasteTile({wasteId, ...rest}: Omit<WasteTileProps, "actions">) {
    const mama = useMothership();
    const {data: waste} = useQuery(watchWasteByIdTqo(mama, {wasteId}));
    const {mutateAsync: sendPatch} = usePatchWasteMutation();
    const xManiNumber = waste?.manifestNumber ?? "";
    const [maniNumber, setManiNumber]
        = useState(xManiNumber);
    const onManiNumberChange: ChangeEventHandler<HTMLInputElement> = (evt) => {
        setManiNumber(evt.target.value);
    }
    const xBags = fixedPointToStr(waste?.bags ?? 0, 0);
    const [bags, setBags] = useState(xBags);

    const performSend = useCallback(() => {
        const patch: WastePatch = {};
        if (maniNumber !== xManiNumber) {
            patch.manifestNumber = maniNumber;
        }
        if (bags !== xBags) {
            patch.bags = strToFixedPoint(bags, 0);
        }

        return sendPatch({wasteId, patch});
    }, [bags, maniNumber, sendPatch, wasteId, xBags, xManiNumber]);

    const {t} = useTranslation();

    return <WasteTile wasteId={wasteId} {...rest}
                      actions={<>
                          <ActionButton variant="solid" color="primary"
                                        sx={TILE_ACTION_BUTTON_SX}
                                        disabled={maniNumber === xManiNumber && bags === xBags}
                                        perform={performSend}>
                              {t("driver.doChange")}
                          </ActionButton>
                      </>}>
        <Stack direction="column" sx={{alignItems: "center"}} spacing={0.4}>
            <JoyInput value={maniNumber}
                      placeholder={t("manifestNumber")}
                      onChange={onManiNumberChange} sx={{width: "90%"}}/>
            <NumericFormat
                decimalScale={0}
                value={bags}
                onValueChange={(values) => {
                    setBags(values.value);
                }}
                customInput={JoyInput}
                placeholder={t("bags")}
                slotProps={{input: {type: "number"}}}
                sx={{width: "90%"}}
                endDecorator={t("bundleSurchargeForm.bagSuffix")}
                valueIsNumericString={true}
            />
        </Stack>
    </WasteTile>
}

export interface DriverUnbundledWasteTileProps {
    wasteId: string;
    potentialBundleId: string;
}

export function DriverUnbundledWasteTile({wasteId, potentialBundleId}: DriverUnbundledWasteTileProps) {
    const mama = useMothership();
    const {t} = useTranslation();
    const {mutateAsync: stampWaste} = useStampWasteMutation();
    const performAdopt = useCallback(() => {
        return mama.attachWasteToBundle({wasteId, bundleId: potentialBundleId})
            .then(() => {
                return stampWaste({food: {wasteId, flavor: WasteStampFlavor.enum.ONLOADED}});
            });
    }, [mama, potentialBundleId, stampWaste, wasteId]);
    const adoptButton = useMemo(() =>
        <ActionButton perform={performAdopt} color="primary" variant="solid"
                      sx={TILE_ACTION_BUTTON_SX}>
            {t("driver.adopt")}
        </ActionButton>, [performAdopt, t]);

    return <WasteTile wasteId={wasteId} actions={adoptButton}/>
}